/**
 * Footer controls for a <slideshow-component> carousel (used by sections/collection-carousel.liquid).
 *
 * <carousel-progress> — a thin scroll-position indicator.
 *   Exposes two CSS custom properties:
 *     --progress-size      fraction of the track the thumb should cover (0..1)
 *     --progress-position  fraction of the remaining track the thumb is offset by (0..1)
 *   Sets `data-disabled` when nothing overflows so CSS can hide it.
 *
 * <carousel-arrows> — prev/next buttons that move ONE slide at a time.
 *   The theme's own arrows advance by a full page (all visible slides); these call
 *   `slideshow.select(current ± 1)` instead. Buttons are matched via [data-action="previous|next"]
 *   and are disabled at the scroll edges. Sets `data-disabled` when nothing overflows.
 *
 * Both elements must be placed inside the <slideshow-component> they control.
 */

/**
 * Finds what the controls act on. Normally that is the theme's <slideshow-component>, but a
 * plain scroll-snap row can opt in too by marking its wrapper [data-carousel] and the scrolling
 * element [data-carousel-scroller] — <carousel-progress> only ever needs something that
 * scrolls. <carousel-arrows> still requires a real slideshow and stays inert without one.
 *
 * @param {HTMLElement} el
 * @returns {{ slideshow: HTMLElement | null, scroller: HTMLElement | null }}
 */
function findSlideshow(el) {
  const slideshow = el.closest('slideshow-component');
  if (slideshow) {
    return { slideshow, scroller: slideshow.querySelector('slideshow-slides') };
  }

  const carousel = el.closest('[data-carousel]');
  return { slideshow: null, scroller: carousel?.querySelector('[data-carousel-scroller]') ?? null };
}

/** @param {HTMLElement} scroller */
function scrollState(scroller) {
  const { scrollWidth, clientWidth, scrollLeft } = scroller;
  const maxScroll = scrollWidth - clientWidth;
  return { maxScroll, position: Math.abs(scrollLeft), hasOverflow: maxScroll > 1 };
}

class CarouselProgress extends HTMLElement {
  /** @type {HTMLElement | null} */
  #scroller = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    this.#scroller = findSlideshow(this).scroller;
    if (!this.#scroller) return;

    this.#scroller.addEventListener('scroll', this.#update, { passive: true });
    this.#resizeObserver = new ResizeObserver(this.#update);
    this.#resizeObserver.observe(this.#scroller);
    this.#update();
  }

  disconnectedCallback() {
    this.#scroller?.removeEventListener('scroll', this.#update);
    this.#resizeObserver?.disconnect();
    this.#scroller = null;
    this.#resizeObserver = null;
  }

  #update = () => {
    if (!this.#scroller) return;

    const { maxScroll, position, hasOverflow } = scrollState(this.#scroller);
    const { scrollWidth, clientWidth } = this.#scroller;

    const size = hasOverflow ? clientWidth / scrollWidth : 1;
    const progress = hasOverflow ? Math.min(Math.max(position / maxScroll, 0), 1) : 0;

    this.style.setProperty('--progress-size', String(size));
    this.style.setProperty('--progress-position', String(progress));
    this.toggleAttribute('data-disabled', !hasOverflow);
  };
}

class CarouselArrows extends HTMLElement {
  /** @type {HTMLElement | null} */
  #slideshow = null;
  /** @type {HTMLElement | null} */
  #scroller = null;
  /** @type {HTMLButtonElement | null} */
  #previous = null;
  /** @type {HTMLButtonElement | null} */
  #next = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    const { slideshow, scroller } = findSlideshow(this);
    this.#slideshow = slideshow;
    this.#scroller = scroller;
    this.#previous = this.querySelector('[data-action="previous"]');
    this.#next = this.querySelector('[data-action="next"]');
    if (!this.#slideshow || !this.#scroller) return;

    this.#previous?.addEventListener('click', this.#goPrevious);
    this.#next?.addEventListener('click', this.#goNext);
    this.#scroller.addEventListener('scroll', this.#update, { passive: true });
    this.#resizeObserver = new ResizeObserver(this.#update);
    this.#resizeObserver.observe(this.#scroller);
    this.#update();
  }

  disconnectedCallback() {
    this.#previous?.removeEventListener('click', this.#goPrevious);
    this.#next?.removeEventListener('click', this.#goNext);
    this.#scroller?.removeEventListener('scroll', this.#update);
    this.#resizeObserver?.disconnect();
    this.#slideshow = null;
    this.#scroller = null;
    this.#resizeObserver = null;
  }

  /** @param {number} direction -1 | 1 */
  #step(direction, event) {
    const slideshow = /** @type {any} */ (this.#slideshow);
    if (!slideshow || typeof slideshow.select !== 'function') return;

    const count = this.#scroller?.querySelectorAll(':scope > slideshow-slide').length ?? 0;
    const current = Number(slideshow.current) || 0;
    const target = Math.min(Math.max(current + direction, 0), count - 1);

    if (target !== current) slideshow.select(target, event);
  }

  #goPrevious = (event) => {
    event.preventDefault();
    this.#step(-1, event);
  };

  #goNext = (event) => {
    event.preventDefault();
    this.#step(1, event);
  };

  #update = () => {
    if (!this.#scroller) return;

    const { maxScroll, position, hasOverflow } = scrollState(this.#scroller);

    if (this.#previous) this.#previous.disabled = !hasOverflow || position <= 1;
    if (this.#next) this.#next.disabled = !hasOverflow || position >= maxScroll - 1;
    this.toggleAttribute('data-disabled', !hasOverflow);
  };
}

if (!customElements.get('carousel-progress')) {
  customElements.define('carousel-progress', CarouselProgress);
}

if (!customElements.get('carousel-arrows')) {
  customElements.define('carousel-arrows', CarouselArrows);
}
